import React, { useState } from "react";
import styled from "styled-components";
import { theme } from "../theme";

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: ${theme.radius.lg}px;
  box-shadow: ${theme.shadows.sm};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${theme.colors.surface};
  border-radius: ${theme.radius.lg}px;
  overflow: hidden;
`;

const Thead = styled.thead`
  background: ${theme.colors.bg};

  tr {
    th:last-child {
      text-align: end;
    }
  }
`;

const Tbody = styled.tbody`
  > tr:not(.drop-zone-row) {
    &:hover {
      background: ${theme.colors.bg};
    }
  }
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: ${theme.colors.textSecondary};
  font-family: ${theme.fonts.body};
  border-bottom: 1px solid ${theme.colors.borderLight};
`;

const Td = styled.td`
  padding: 10px 16px;
  border-bottom: 1px solid ${theme.colors.borderLight};
  font-size: 14px;
  color: ${theme.colors.text};
  font-family: ${theme.fonts.body};

  &:last-child {
    text-align: end;
  }
`;

const Tr = styled.tr`
  cursor: ${(props) => (props.$isReorderable ? "grab" : "default")};
  user-select: ${(props) => (props.$isReorderable ? "none" : "auto")};
`;

const DropZone = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  font-size: 13px;
  font-weight: 500;
  color: ${(props) =>
    props.$isOver ? theme.colors.accent : theme.colors.textSecondary};
  background-color: ${(props) =>
    props.$isOver
      ? "#e8f2ff"
      : props.$active
        ? theme.colors.bg
        : "transparent"};
  border: ${(props) =>
    props.$active
      ? props.$isOver
        ? `2px dashed ${theme.colors.accent}`
        : `2px dashed ${theme.colors.border}`
      : "none"};
  border-radius: ${theme.radius.sm}px;
  height: ${(props) =>
    props.$active ? (props.$isOver ? "36px" : "22px") : "0px"};
  margin: ${(props) => (props.$active ? "4px 8px" : "0")};
  opacity: ${(props) => (props.$active ? 1 : 0)};
  overflow: hidden;
  transition: all 0.15s ease-in-out;
  pointer-events: ${(props) => (props.$active ? "all" : "none")};
  user-select: none;

  * {
    pointer-events: none;
  }
`;

const EditButton = styled.button`
  padding: 6px 12px;
  margin-right: 5px;
  border: none;
  border-radius: ${theme.radius.sm}px;
  cursor: pointer;
  font-size: 12px;
  font-family: ${theme.fonts.body};
  transition: background 0.2s;
  background: ${theme.colors.accent};
  color: #fff;
  font-weight: 500;

  &:hover {
    background: ${theme.colors.accentHover};
  }
`;

const DeleteButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: ${theme.radius.sm}px;
  cursor: pointer;
  font-size: 12px;
  font-family: ${theme.fonts.body};
  transition: background 0.2s;
  background: ${theme.colors.danger};
  color: #fff;
  font-weight: 500;

  &:not(:disabled):hover {
    background: ${theme.colors.dangerHover};
  }
`;

const EmptyMessage = styled.p`
  text-align: center;
  padding: 30px;
  color: ${theme.colors.textSecondary};
  font-family: ${theme.fonts.body};
`;

const AdminTable = ({
  columns,
  data,
  onEdit,
  onDelete,
  isReorderable = false,
  onReorder,
  idKey = "id",
}) => {
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [activeDropZone, setActiveDropZone] = useState(null);

  if (!data || data.length === 0) {
    return <EmptyMessage>Нет данных</EmptyMessage>;
  }

  const handleDragStart = (e, item) => {
    if (!isReorderable) return;
    setDraggedItemId(item[idKey]);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleZoneDragOver = (e, zoneIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (activeDropZone !== zoneIndex) {
      setActiveDropZone(zoneIndex);
    }
  };

  const handleZoneDragLeave = (zoneIndex) => {
    if (activeDropZone === zoneIndex) {
      setActiveDropZone(null);
    }
  };

  const handleZoneDrop = async (e, targetZoneIndex) => {
    e.preventDefault();
    setActiveDropZone(null);

    if (draggedItemId === null || !onReorder) return;

    const currentDragged = draggedItemId;
    setDraggedItemId(null);

    const draggedIndex = data.findIndex(
      (item) => item[idKey] === currentDragged,
    );

    if (
      draggedIndex === -1 ||
      targetZoneIndex === draggedIndex ||
      targetZoneIndex === draggedIndex + 1
    ) {
      return;
    }

    const newOrder = [...data];
    const [draggedItem] = newOrder.splice(draggedIndex, 1);
    const newIndex =
      targetZoneIndex > draggedIndex ? targetZoneIndex - 1 : targetZoneIndex;
    newOrder.splice(newIndex, 0, draggedItem);

    const updatedData = newOrder.map((item, idx) => ({
      ...item,
      display_order: idx,
    }));

    onReorder(updatedData);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
    setActiveDropZone(null);
  };

  return (
    <TableContainer>
      <Table>
        <Thead>
          <Tr>
            {columns.map((col) => (
              <Th key={col.accessor}>{col.Header}</Th>
            ))}
            <Th>Действия</Th>
          </Tr>
        </Thead>
        <Tbody>
          {isReorderable && (
            <tr className="drop-zone-row" style={{ border: "none" }}>
              <td
                colSpan={columns.length + 1}
                style={{ padding: 0, border: "none" }}
              >
                <DropZone
                  $active={draggedItemId !== null}
                  $isOver={activeDropZone === 0}
                  onDragOver={(e) => handleZoneDragOver(e, 0)}
                  onDragLeave={() => handleZoneDragLeave(0)}
                  onDrop={(e) => handleZoneDrop(e, 0)}
                >
                  {activeDropZone === 0
                    ? "Вставить в начало"
                    : draggedItemId !== null
                      ? "Зона переноса"
                      : ""}
                </DropZone>
              </td>
            </tr>
          )}
          {data.map((row, index) => (
            <React.Fragment key={row[idKey] || index}>
              <Tr
                $isReorderable={isReorderable}
                draggable={isReorderable}
                onDragStart={(e) => handleDragStart(e, row)}
                onDragEnd={handleDragEnd}
                style={{
                  opacity: draggedItemId === row[idKey] ? 0.4 : 1,
                }}
              >
                {columns.map((col) => (
                  <Td key={col.accessor}>
                    {col.Cell ? col.Cell(row) : row[col.accessor]}
                  </Td>
                ))}
                <Td>
                  <EditButton onClick={() => onEdit(row)}>
                    Редактировать
                  </EditButton>
                  {onDelete && (
                    <DeleteButton onClick={() => onDelete(row)}>
                      Удалить
                    </DeleteButton>
                  )}
                </Td>
              </Tr>
              {isReorderable && (
                <tr className="drop-zone-row" style={{ border: "none" }}>
                  <td
                    colSpan={columns.length + 1}
                    style={{ padding: 0, border: "none" }}
                  >
                    <DropZone
                      $active={draggedItemId !== null}
                      $isOver={activeDropZone === index + 1}
                      onDragOver={(e) => handleZoneDragOver(e, index + 1)}
                      onDragLeave={() => handleZoneDragLeave(index + 1)}
                      onDrop={(e) => handleZoneDrop(e, index + 1)}
                    >
                      {activeDropZone === index + 1
                        ? index === data.length - 1
                          ? "Вставить в конец"
                          : "Вставить сюда"
                        : draggedItemId !== null
                          ? "Зона переноса"
                          : ""}
                    </DropZone>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default AdminTable;
